import { Observable, OperatorFunction } from "rxjs";
import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";

export function getDateFromString(date: string | null): Date | null {
  if (date) {
    const [day, month, year] = date.split("/");
    const dateConverted = new Date(+year, Number.parseFloat(month) - 1, +day);
    if (!isNaN(dateConverted.getTime())) {
      return dateConverted;
    }
  }
  return null;
}

export function buildSearchFunction(
  mappingFunction: (term: string) => string[],
): OperatorFunction<string, readonly string[]> {
  return (text$: Observable<string>) => text$.pipe(debounceTime(200), distinctUntilChanged(), map(mappingFunction));
}

export function getEnumKeyByValue(enumObject: any, value: any, defaultValue?: string): string | undefined {
  return (Object.keys(enumObject).find((key) => enumObject[key] === value) ?? defaultValue) ?? undefined;
}

export function formatDateToYYYYMMDDHHMMSS(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day} 00:00:00`;
}

export function formatDateToDDMMYYYY(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${day}/${month}/${year}`;
}

export function capitalizeFirstCharacter(text: string | null | undefined): string {
  if (!text) {
    return "";
  }
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}

/**
 * Calculates the week number for a given date according to ISO 8601 standard.
 * The first week of the year is the week that contains the first Thursday of the year.
 *
 * @param date - The date for which to calculate the week number
 * @returns The week number (between 1 and 53)
 */
export function getWeekNumber(date: Date): number {
  const tempDate = new Date(date.getTime());

  // By using (tempDate.getDay() || 7), we ensure that Sunday is treated as the 7th day.
  tempDate.setDate(tempDate.getDate() + 4 - (tempDate.getDay() || 7));

  // Get the first day of the year
  const yearStart = new Date(tempDate.getFullYear(), 0, 1);

  // Calculate the number of weeks between the two dates
  return Math.ceil((((tempDate.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
}
